import { BoardDisplaySelect } from "@design-system/Board/BoardDisplaySelect";
import type { BoardCardFieldOption } from "@design-system/Board/Board.types";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { VStack } from "@design-system/VStack/VStack";
import { toTaskBoardField, type TaskBoardField } from "./taskBoardActions";
import {
    CARD_FIELD_PLACEMENT_OPTIONS,
    toCardFieldPlacement,
    type TaskCardFieldPlacements,
} from "./taskBoardCardFields";
import styles from "./BoardPlayground.module.scss";

/** Fields every card shows, so they have no placement. */
const FIXED_FIELDS: readonly BoardCardFieldOption[] = [
    { id: "title", label: "Title" },
    { id: "status", label: "Status" },
];

interface BoardPlaygroundCardFieldControlsProps {
    /** Optional card fields the Display popover shows. */
    visibleCardFields: readonly BoardCardFieldOption[];
    placements: TaskCardFieldPlacements;
    editableFieldIds: ReadonlySet<TaskBoardField>;
    onPlacementsChange: (placements: TaskCardFieldPlacements) => void;
    onEditableFieldIdsChange: (fieldIds: ReadonlySet<TaskBoardField>) => void;
}

/**
 * The Controls panel's card fields: where each visible field sits, and an
 * Editable checkbox for each field that has an inline editor (title,
 * status, entity, due date, priority, assignee). Fields without one show
 * none.
 */
export const BoardPlaygroundCardFieldControls = ({
    visibleCardFields,
    placements,
    editableFieldIds,
    onPlacementsChange,
    onEditableFieldIdsChange,
}: Readonly<BoardPlaygroundCardFieldControlsProps>) => {
    const renderEditable = ({ id, label }: BoardCardFieldOption) => {
        const field = toTaskBoardField(id);
        return (
            field !== undefined && (
                <Checkbox
                    label="Editable"
                    aria-label={`${label} editable`}
                    checked={editableFieldIds.has(field)}
                    onCheckedChange={(isEditable) => {
                        const next = new Set(editableFieldIds);
                        if (isEditable) {
                            next.add(field);
                        } else {
                            next.delete(field);
                        }
                        onEditableFieldIdsChange(next);
                    }}
                />
            )
        );
    };

    return (
        <VStack as="section" gap={12}>
            <h3 className={styles.controlsHeading}>Card fields</h3>
            {FIXED_FIELDS.map((option) => (
                <VStack key={option.id} gap={6}>
                    <span className={styles.controlsFieldLabel}>
                        {option.label}
                    </span>
                    {renderEditable(option)}
                </VStack>
            ))}
            {visibleCardFields.map((option) => (
                <VStack key={option.id} gap={6}>
                    <BoardDisplaySelect
                        label={option.label}
                        items={CARD_FIELD_PLACEMENT_OPTIONS}
                        value={placements[option.id]}
                        onValueChange={(value) => {
                            const placement = toCardFieldPlacement(value);
                            if (placement) {
                                onPlacementsChange({
                                    ...placements,
                                    [option.id]: placement,
                                });
                            }
                        }}
                    />
                    {renderEditable(option)}
                </VStack>
            ))}
        </VStack>
    );
};
